import { makeAutoObservable, runInAction } from 'mobx'
import { normalizePhoneForMask } from 'presentation/utils/phone-utils'
import { formatCpf } from 'common/utils/format-cpf'
import { validateCpf } from 'presentation/utils/validators/cpf-validator'
import { HospitalNew } from 'domain/entities/hospital-model-new'
import { SurgeryCenterNew } from 'domain/entities/surgery-center-model-new'
import { GetAllHealthPlans } from 'domain/usecases/patient/get-all-health-plans'
import { GetHealthSubPlans } from 'domain/usecases/patient/get-health-sub-plans'
import moment from 'moment-timezone'
import { v4 as uuidv4 } from 'uuid'

import {
  CidV2Props,
  CooperativeV2Props,
  HealthInsuranceV2Props,
  HospitalServiceContract,
  LocationV2Props,
  OpmeV2Props,
  ORDERED_STEPS,
  OrderStepsPath,
  PatientServiceContract,
  PatientV2Props,
  ProcedureV2Props,
  ProviderV2Props,
  RegistrationInfoFormValues,
  ClinicalHistoryFormValues,
  SurgicalOrderServiceContract,
  SurgicalOrderV2Props
} from './types'

import {
  INITIAL_CLINICAL_HISTORY,
  INITIAL_COOPERATIVE,
  INITIAL_HEALTH_INSURANCE,
  INITIAL_LOCATION,
  INITIAL_PATIENT,
  INITIAL_SURGICAL_ORDER,
  INITIAL_PROCEDURES,
  INITIAL_PROVIDERS,
  INITIAL_OPMES,
  INITIAL_CIDS
} from './initial-state'
import { UpdateSurgicalOrderNew } from 'domain/usecases/surgical-order/update-surgical-order-new'
import { CreateSurgicalOrder } from 'domain/usecases/surgical-order/create-surgical-order'
import { MaritalStatus } from 'presentation/utils/default-marital-status'
import { Techniques } from 'common/enum/techniques'
import { HospitalizationType } from 'common/enum/hospitalization-type'
import { HospitalizationMode } from 'common/enum/hospitalization-mode'
import { FinishSurgicalOrder } from 'domain/usecases/surgical-order/finish-surgical-order'
import { GenderNew } from 'common/enum/gender'
import { isPatientRN } from 'presentation/utils/validators/is-patient-rn'

export type {
  LocationV2Props,
  PatientV2Props,
  HealthInsuranceV2Props,
  CooperativeV2Props,
  SurgicalOrderV2Props,
  RegistrationInfoFormValues,
  ClinicalHistoryFormValues
}

export class SurgicalOrderV2 {
  location: LocationV2Props = { ...INITIAL_LOCATION }
  patient: PatientV2Props = { ...INITIAL_PATIENT }
  healthInsurance: HealthInsuranceV2Props = { ...INITIAL_HEALTH_INSURANCE }
  cooperative: CooperativeV2Props = { ...INITIAL_COOPERATIVE }
  clinicalHistory: ClinicalHistoryFormValues = { ...INITIAL_CLINICAL_HISTORY }
  procedures: ProcedureV2Props[] = [...INITIAL_PROCEDURES]
  providers: ProviderV2Props[] = [...INITIAL_PROVIDERS]
  opmes: OpmeV2Props[] = [...INITIAL_OPMES]
  opmeJustification = ''
  cids: CidV2Props[] = [...INITIAL_CIDS]
  surgicalOrder: SurgicalOrderV2Props = { ...INITIAL_SURGICAL_ORDER }
  submitFormTriggered = 0
  saveOnlyTriggered = 0
  formKey = 0
  isStepValid = false
  currentStep: OrderStepsPath = OrderStepsPath.REGISTRATION
  completedSteps: number[] = []
  isLoadingSurgicalOrder = false
  isLoadingPatient = false
  isLoadingTutor = false
  isLoadingHospitals = false
  isLoadingSurgeryCenters = false
  isLoadingHealthPlans = false

  // Estado de referência para os selects do formulário
  hospitals: HospitalNew[] = []
  surgeryCenters: SurgeryCenterNew[] = []
  healthPlans: GetAllHealthPlans.Model = []
  healthSubPlans: GetHealthSubPlans.Model = []
  isLoadingSubPlans = false

  // Estado de UI da busca de paciente
  patientSearched = false
  patientFound = false
  tutorSearched = false
  tutorFound = false

  // Estado de documentos pendentes de processamento
  pendingDocumentIds: string[] = []
  isVerifyingDocuments = false

  constructor() {
    makeAutoObservable(this)
  }

  triggerSubmit() {
    this.submitFormTriggered++
  }

  triggerSaveOnly() {
    this.saveOnlyTriggered++
  }

  setIsStepValid(val: boolean) {
    this.isStepValid = val
  }

  setCurrentStep(step: OrderStepsPath) {
    this.currentStep = step
  }

  markStepCompleted(stepIndex: number) {
    if (!this.completedSteps.includes(stepIndex)) {
      this.completedSteps.push(stepIndex)
    }
  }

  unmarkStepCompleted(stepIndex: number) {
    this.completedSteps = this.completedSteps.filter((s) => s !== stepIndex)
  }

  updateLocation(partial: Partial<LocationV2Props>) {
    Object.assign(this.location, partial)
  }

  updatePatient(partial: Partial<PatientV2Props>) {
    Object.assign(this.patient, partial)
  }

  updateHealthInsurance(partial: Partial<HealthInsuranceV2Props>) {
    Object.assign(this.healthInsurance, partial)
  }

  updateCooperative(partial: Partial<CooperativeV2Props>) {
    Object.assign(this.cooperative, partial)
  }

  updateClinicalHistory(partial: Partial<ClinicalHistoryFormValues>) {
    Object.assign(this.clinicalHistory, partial)
  }

  updateProcedures(procedures: ProcedureV2Props[]) {
    this.procedures = procedures
  }

  updateProviders(providers: ProviderV2Props[]) {
    this.providers = providers
  }

  updateOpmes(opmes: OpmeV2Props[]) {
    this.opmes = opmes
  }

  updateOpmeJustification(justification: string) {
    this.opmeJustification = justification
  }

  updateCids(cids: CidV2Props[]) {
    this.cids = cids
  }

  updateStoreSurgicalOrder(partial: Partial<SurgicalOrderV2Props>) {
    Object.assign(this.surgicalOrder, partial)
  }

  // Métodos para gerenciar documentos pendentes de processamento
  addPendingDocument(documentId: string) {
    if (!this.pendingDocumentIds.includes(documentId)) {
      this.pendingDocumentIds.push(documentId)
    }
  }

  removePendingDocument(documentId: string) {
    this.pendingDocumentIds = this.pendingDocumentIds.filter(
      (id) => id !== documentId
    )
  }

  clearPendingDocuments() {
    this.pendingDocumentIds = []
  }

  get hasPendingDocuments() {
    return this.pendingDocumentIds.length > 0
  }

  setIsVerifyingDocuments(val: boolean) {
    this.isVerifyingDocuments = val
  }

  resetLocation() {
    this.location = { ...INITIAL_LOCATION }
  }

  resetPatient() {
    this.patient = { ...INITIAL_PATIENT }
  }

  resetTutor() {
    this.patient.tutor = { ...INITIAL_PATIENT.tutor }
  }

  resetHealthInsurance() {
    this.healthInsurance = { ...INITIAL_HEALTH_INSURANCE }
  }

  resetCooperative() {
    this.cooperative = { ...INITIAL_COOPERATIVE }
  }

  resetClinicalHistory() {
    this.clinicalHistory = { ...INITIAL_CLINICAL_HISTORY }
  }

  resetSurgicalOrder() {
    this.surgicalOrder = { ...INITIAL_SURGICAL_ORDER }
  }

  resetSubmitTrigger() {
    this.submitFormTriggered = 0
  }

  resetSaveOnlyTrigger() {
    this.saveOnlyTriggered = 0
  }

  resetIsStepValid() {
    this.isStepValid = false
  }

  resetProcedures() {
    this.procedures = [...INITIAL_PROCEDURES]
  }

  resetProviders() {
    this.providers = [...INITIAL_PROVIDERS]
  }

  resetOpmes() {
    this.opmes = [...INITIAL_OPMES]
  }

  resetOpmeJustification() {
    this.opmeJustification = ''
  }

  resetCids() {
    this.cids = [...INITIAL_CIDS]
  }

  private updateCompletedStepsFromLoadedData() {
    const isRegistrationComplete =
      !!this.location.hospital?.hospitalId &&
      !!this.location.surgical_center?.surgeryCenterId &&
      (!!this.patient.cpf || !!this.patient.patientId) &&
      !!this.healthInsurance.healthInsuranceCode &&
      !!this.healthInsurance.healthCard

    if (isRegistrationComplete) {
      this.markStepCompleted(ORDERED_STEPS.indexOf(OrderStepsPath.REGISTRATION))
    }

    const isProceduresComplete =
      this.hasProcedures &&
      this.hasMainProcedure &&
      this.hasCids &&
      this.hasMainCid

    if (isProceduresComplete) {
      this.markStepCompleted(ORDERED_STEPS.indexOf(OrderStepsPath.PROCEDURE))
    }

    // Perguntas sim/não: "Não" (false) é uma resposta válida e completa.
    // Por isso usamos `typeof === 'boolean'` em vez de `!!`, que trataria
    // `false` como "não preenchido" e marcaria o step como incompleto.
    //
    // Diárias: para o tipo "Ambulatorial" a quantidade válida é 0; para os
    // demais tipos a quantidade mínima é 1.
    const dailyQuantity =
      this.clinicalHistory.clinicalHistoryHospitalizationDailyQuantity
    const isDailyQuantityValid =
      typeof dailyQuantity === 'number' &&
      (this.clinicalHistory.clinicalHistoryHospitalizationMode === 'ambulatory'
        ? dailyQuantity >= 0
        : dailyQuantity >= 1)

    const isClinicalHistoryComplete =
      !!this.clinicalHistory.clinicalHistoryDescription &&
      !!this.clinicalHistory.clinicalHistoryAspects &&
      !!this.clinicalHistory.clinicalHistoryLatexAllergy &&
      typeof this.clinicalHistory.clinicalHistoryOncology === 'boolean' &&
      typeof this.clinicalHistory.clinicalHistoryBloodTransfusion ===
        'boolean' &&
      !!this.clinicalHistory.clinicalHistoryTechnique &&
      typeof this.clinicalHistory.clinicalHistoryCongelation === 'boolean' &&
      typeof this.clinicalHistory.clinicalHistoryBloodReserve === 'boolean' &&
      !!this.clinicalHistory.clinicalHistoryHospitalizationType &&
      !!this.clinicalHistory.clinicalHistoryHospitalizationMode &&
      isDailyQuantityValid &&
      typeof this.clinicalHistory.clinicalHistoryCti === 'boolean' &&
      !!this.clinicalHistory.clinicalHistoryMedicalObservations

    if (isClinicalHistoryComplete) {
      this.markStepCompleted(
        ORDERED_STEPS.indexOf(OrderStepsPath.CLINICAL_HISTORY)
      )
    }

    const isReportsAndDocumentsComplete =
      (this.surgicalOrder.documents?.length ?? 0) > 0 ||
      !!this.surgicalOrder.reportRecommendation?.trim()

    if (isReportsAndDocumentsComplete) {
      this.markStepCompleted(
        ORDERED_STEPS.indexOf(OrderStepsPath.REPORTS_AND_DOCUMENTS)
      )
    }
  }

  async loadSurgicalOrder(
    surgicalOrderId: number,
    service: SurgicalOrderServiceContract
  ) {
    this.isLoadingSurgicalOrder = true
    try {
      const response = await service.getSurgicalOrderById({
        surgical_order_id: surgicalOrderId
      })

      runInAction(() => {
        // TODO (Possível melhoria): Substituir os updates pelo "syncStoreFromParams"
        this.updateStoreSurgicalOrder({
          surgicalOrderId: response.surgical_order_id,
          orderWasLoadedInContext: true,
          documents: response.documents,
          reportRecommendation: response.report_recommendation
        })

        this.updateLocation({
          hospital: {
            hospitalId: response.hospital.hospital_id,
            hospitalName: response.hospital.name
          },
          surgical_center: {
            surgeryCenterId: response.surgical_center.surgical_center_id || 0,
            surgeryCenterName: response.surgical_center.surgical_center || ''
          }
        })

        this.updatePatient({
          cpf: response.patient?.user?.cpf
            ? formatCpf(response.patient.user.cpf)
            : '',
          patientId: response.patient?.patient_id
        })

        this.updateHealthInsurance({
          healthInsuranceId: response.health_insurance?.health_insurance_id,
          healthInsuranceName: response.health_insurance?.health_insurance_name,
          healthInsuranceCode: response.health_insurance?.health_insurance_code,
          healthCard: response.health_insurance?.health_insurance_card,
          validThru: response.health_insurance?.valid_thru,
          healthPlanName: response.health_insurance?.health_plan_name,
          healthPlanCode: response.health_insurance?.health_plan_code,
          healthSubplanName: response.health_insurance?.health_sub_plan_name,
          healthSubplanCode: response.health_insurance?.health_sub_plan_code,
          companyName: response.health_insurance?.company_name
        })

        this.updateCooperative({
          cooperative: response.cooperative_name
        })

        this.updateClinicalHistory({
          clinicalHistoryDescription:
            response.clinical_history?.clinical_history,
          clinicalHistoryAspects:
            response.clinical_history?.psychological_aspects,
          clinicalHistoryLatexAllergy:
            response.clinical_history?.allergy?.[0] || '',
          clinicalHistoryOtherAllergy:
            response.clinical_history?.allergy?.[1] || '',
          clinicalHistoryOncology:
            response.clinical_record?.oncology_or_research,
          clinicalHistoryBloodTransfusion:
            response.clinical_history?.blood_transfusion,
          clinicalHistoryTechnique: response.clinical_record
            ?.technique as Techniques,
          clinicalHistoryCongelation: response.clinical_record?.freeze,
          clinicalHistoryBloodReserve: response.clinical_record?.extra_blood,
          clinicalHistoryHospitalizationType: response.clinical_record
            ?.hospitalization_type as HospitalizationType,
          clinicalHistoryHospitalizationMode:
            response.clinical_record?.hospitalization_mode,
          clinicalHistoryHospitalizationDailyQuantity:
            response.clinical_record?.requested_dailies,
          clinicalHistoryExpectedDate: response?.expected_date,
          clinicalHistoryBullishForecastDate:
            response.clinical_record?.bullish_forecast_date,
          clinicalHistoryCti: response.clinical_record?.cti,
          clinicalHistoryMedicalObservations:
            response.clinical_record.medical_observation
        })

        if (response.procedures && response.procedures.length > 0) {
          this.updateProcedures(
            response.procedures.map((procedure) => {
              return {
                code: procedure.code,
                description: procedure.description,
                quantity: procedure.quantity,
                doctor_name: procedure.doctor_name,
                pro_fat_id: procedure.pro_fat_id,
                surgery_id: procedure.surgery_id,
                is_main: procedure?.is_main || procedure.isMain,
                is_selected: procedure?.is_selected,
                source: procedure?.source
              }
            })
          )

          if (response.cids && response.cids.length > 0) {
            this.updateCids(
              response.cids.map((cid) => {
                return {
                  code: cid.code,
                  description: cid.description,
                  is_main: cid?.is_main || false
                }
              })
            )
          }

          if (response.opme) {
            if (response.opme.solicitations.length > 0) {
              this.updateOpmes(
                response.opme.solicitations.map((opme) => {
                  return {
                    code: opme?.code || uuidv4(),
                    description: opme.description,
                    quantity: opme.quantity,
                    is_selected: opme?.is_selected,
                    source: opme?.source,
                    low_acceptance: opme?.low_acceptance
                  }
                })
              )
            }

            if (
              response.opme.providers_new &&
              response.opme.providers_new.length > 0
            ) {
              this.updateProviders(
                response.opme.providers_new.map((provider) => {
                  return {
                    code: uuidv4(),
                    name: provider.name,
                    reason: provider?.reason,
                    is_selected: provider?.is_selected,
                    source: provider?.source
                  }
                })
              )
            }

            this.updateOpmeJustification(response.opme.denied_reason || '')
          }
        }

        this.updateCompletedStepsFromLoadedData()
      })

      return response
    } finally {
      runInAction(() => {
        this.isLoadingSurgicalOrder = false
      })
    }
  }

  async createSurgicalOrder(
    service: SurgicalOrderServiceContract,
    data: CreateSurgicalOrder.Params
  ) {
    if (this.surgicalOrder?.surgicalOrderId) return

    const response = await service.createSurgicalOrder({
      ...data
    })

    await this.loadSurgicalOrder(response.surgical_order_id, service)
  }

  private syncStoreFromParams(data: UpdateSurgicalOrderNew.Params) {
    if (data.hospital || data.surgical_center) {
      this.updateLocation({
        ...(data.hospital !== undefined && {
          hospital: {
            hospitalId: data.hospital.hospital_id,
            hospitalName: data.hospital.name
          }
        }),
        ...(data.surgical_center !== undefined && {
          surgical_center: {
            surgeryCenterId: data.surgical_center.surgical_center_id!,
            surgeryCenterName: data.surgical_center.surgical_center!
          }
        })
      })
    }

    if (data.patient) {
      this.updatePatient({
        ...(data.patient.user?.cpf && {
          cpf: formatCpf(data.patient.user.cpf)
        }),
        ...(data.patient.patient_id && {
          patientId: data.patient.patient_id
        }),
        ...(data.patient.patient_name && {
          name: data.patient.patient_name
        })
      })
    }

    if (data.health_insurance) {
      this.updateHealthInsurance({
        healthInsuranceId: data.health_insurance.health_insurance_id,
        healthInsuranceName: data.health_insurance.health_insurance_name,
        healthInsuranceCode: data.health_insurance.health_insurance_code,
        healthCard: data.health_insurance.health_insurance_card,
        validThru: data.health_insurance.valid_thru,
        healthPlanName: data.health_insurance.health_plan_name,
        healthPlanCode: data.health_insurance.health_plan_code,
        healthSubplanName: data.health_insurance.health_sub_plan_name,
        healthSubplanCode: data.health_insurance.health_sub_plan_code
      })
    }

    if (data.cooperative_name !== undefined) {
      this.updateCooperative({ cooperative: data.cooperative_name })
    }

    if (data.clinical_history || data.clinical_record) {
      this.updateClinicalHistory({
        ...(data.clinical_history?.clinical_history !== undefined && {
          clinicalHistoryDescription: data.clinical_history.clinical_history
        }),
        ...(data.clinical_history?.psychological_aspects !== undefined && {
          clinicalHistoryAspects: data.clinical_history.psychological_aspects
        }),
        ...(data.clinical_history?.allergy !== undefined && {
          clinicalHistoryLatexAllergy: data.clinical_history.allergy[0],
          clinicalHistoryOtherAllergy: data.clinical_history.allergy[1]
        }),
        ...(data.clinical_history?.blood_transfusion !== undefined && {
          clinicalHistoryBloodTransfusion:
            data.clinical_history.blood_transfusion
        }),
        ...(data.clinical_record?.oncology_or_research !== undefined && {
          clinicalHistoryOncology: data.clinical_record.oncology_or_research
        }),
        ...(data.clinical_record?.freeze !== undefined && {
          clinicalHistoryCongelation: data.clinical_record.freeze
        }),
        ...(data.clinical_record?.extra_blood !== undefined && {
          clinicalHistoryBloodReserve: data.clinical_record.extra_blood
        }),
        ...(data.clinical_record?.technique !== undefined && {
          clinicalHistoryTechnique: data.clinical_record
            .technique as ClinicalHistoryFormValues['clinicalHistoryTechnique']
        }),
        ...(data.clinical_record?.hospitalization_type && {
          clinicalHistoryHospitalizationType: data.clinical_record
            .hospitalization_type as HospitalizationType
        }),
        ...(data?.expected_date && {
          clinicalHistoryExpectedDate: data.expected_date
        }),
        ...(data.clinical_record?.hospitalization_mode !== undefined && {
          clinicalHistoryHospitalizationMode: data.clinical_record
            .hospitalization_mode as HospitalizationMode
        }),
        ...(data.clinical_record?.requested_dailies !== undefined && {
          clinicalHistoryHospitalizationDailyQuantity:
            data.clinical_record.requested_dailies
        }),
        ...(data.clinical_record?.cti !== undefined && {
          clinicalHistoryCti: data.clinical_record.cti
        }),
        ...(data.clinical_record?.bullish_forecast_date !== undefined && {
          clinicalHistoryBullishForecastDate:
            data.clinical_record.bullish_forecast_date
        }),
        ...(data.clinical_record?.medical_observation !== undefined && {
          clinicalHistoryMedicalObservations:
            data.clinical_record.medical_observation
        })
      })
    }

    if (data.report_recommendation) {
      this.updateStoreSurgicalOrder({
        reportRecommendation: data.report_recommendation
      })
    }

    const proceduresData = data.procedures || data.procedure
    if (proceduresData) {
      this.updateProcedures(
        proceduresData.map((procedure) => {
          return {
            code: procedure.code || procedure.procedure_code,
            description: procedure.description,
            quantity: procedure.quantity,
            doctor_name: procedure.doctor_name,
            pro_fat_id: procedure.pro_fat_id,
            surgery_id: procedure.surgery_id,
            is_main: procedure.is_main || procedure.isMain,
            is_selected: procedure.is_selected,
            source: procedure.source
          }
        })
      )
    }

    const cidsData = data.cids || data.cid
    if (cidsData) {
      this.updateCids(
        cidsData.map((cid) => {
          return {
            code: cid.code,
            description: cid.description,
            is_main: cid?.is_main || false
          }
        })
      )
    }

    if (data.opme) {
      this.updateOpmes(
        data.opme.solicitations.map((opme) => {
          return {
            code: opme?.code || uuidv4(),
            description: opme.description,
            quantity: opme.quantity,
            is_selected: opme?.is_selected,
            source: opme?.source,
            low_acceptance: opme?.low_acceptance
          }
        })
      )

      this.updateProviders(
        (data.opme.providers_new || []).map((provider) => {
          return {
            code: uuidv4(),
            name: provider.name,
            reason: provider?.reason,
            is_selected: provider?.is_selected,
            source: provider?.source
          }
        })
      )

      this.updateOpmeJustification(data.opme.denied_reason || '')
    }
  }

  async updateSurgicalOrder(
    service: SurgicalOrderServiceContract,
    data: UpdateSurgicalOrderNew.Params
  ) {
    if (!this.surgicalOrder?.surgicalOrderId) return

    await service.updateSurgicalOrder({ ...data })

    runInAction(() => {
      this.syncStoreFromParams(data)
    })
  }

  async finishSurgicalOrder(
    service: SurgicalOrderServiceContract,
    data: FinishSurgicalOrder.Params
  ) {
    if (!this.surgicalOrder?.surgicalOrderId) return

    const response = await service.finishSurgicalOrder({ ...data })

    runInAction(() => {
      this.surgicalOrder.finalizedSurgicalOrderId = response.surgical_order_id
    })
  }

  async loadHospitals(service: HospitalServiceContract) {
    this.isLoadingHospitals = true
    try {
      const response = await service.loadHospitalsByDoctor()
      runInAction(() => {
        this.hospitals = response ?? []
      })
    } catch (error) {
      console.error('[Store] Erro ao carregar hospitais:', error)
      throw error
    } finally {
      runInAction(() => {
        this.isLoadingHospitals = false
      })
    }
  }

  async loadSurgeryCenters(
    hospitalId: number,
    service: HospitalServiceContract
  ) {
    this.isLoadingSurgeryCenters = true
    try {
      const response = await service.loadSurgeryCenters({
        hospital_id: hospitalId
      })
      runInAction(() => {
        this.surgeryCenters = response ?? []
      })
    } catch (error) {
      console.error('[Store] Erro ao carregar centros cirúrgicos:', error)
      throw error
    } finally {
      runInAction(() => {
        this.isLoadingSurgeryCenters = false
      })
    }
  }

  async loadHealthPlans(service: PatientServiceContract) {
    this.isLoadingHealthPlans = true
    try {
      const response = await service.getAllHealthPlans()
      runInAction(() => {
        this.healthPlans = response ?? []
      })
    } catch (error) {
      console.error('[Store] Erro ao carregar convênios:', error)
      throw error
    } finally {
      runInAction(() => {
        this.isLoadingHealthPlans = false
      })
    }
  }

  async loadSubPlans(
    healthInsuranceId: number,
    planId: number,
    service: PatientServiceContract
  ) {
    this.isLoadingSubPlans = true
    try {
      const response = await service.getHealthSubPlans({
        health_insurance_id: healthInsuranceId,
        plan_id: planId
      })
      runInAction(() => {
        this.healthSubPlans = response ?? []
      })
    } catch (error) {
      console.error('[Store] Erro ao carregar subplanos:', error)
      runInAction(() => {
        this.healthSubPlans = []
      })
    } finally {
      runInAction(() => {
        this.isLoadingSubPlans = false
      })
    }
  }

  async loadPatientByCpf(cpf: string, service: PatientServiceContract) {
    const cleanCpf = cpf.replace(/\D/g, '')

    if (cleanCpf.length !== 11 || !validateCpf(cleanCpf)) {
      runInAction(() => {
        this.patientSearched = false
        this.patientFound = false
        this.tutorSearched = false
        this.tutorFound = false
        this.resetPatient()
        this.resetHealthInsurance()
      })
      return
    }

    this.isLoadingPatient = true
    try {
      const response = await service.getPatientByCpfOrName({ cpf: cleanCpf })

      if (response && !!response?.[0]) {
        const found = response[0]

        if (
          found?.birthday &&
          isPatientRN(moment(found?.birthday).utc().toDate())
        ) {
          await this.loadPatientById(found.patient_id, service)
        } else {
          runInAction(() => {
            this.patientSearched = true
            this.patientFound = true
            this.updatePatient({
              patientId: found?.patient_id,
              cpf: found.user?.cpf && formatCpf(found.user.cpf),
              name: found.name,
              birthDate: found.birthday,
              gender: found.gender,
              maritalStatus: found.marital_status as MaritalStatus,
              phone: found.user?.phone || found.tutor?.phone,
              email: found.user?.email || found.tutor?.email,
              isNewPatient: false,
              internation: found.internation,
              ...(found?.tutor && {
                tutor: {
                  cpf: found.tutor?.cpf,
                  name: found.tutor?.name,
                  phone: found.tutor?.phone,
                  email: found.tutor?.email
                }
              })
            })
          })

          if (
            found?.patient_id &&
            !this.surgicalOrder.orderWasLoadedInContext
          ) {
            await this.loadPatientHealthInsurance(found.patient_id, service)
          }
        }
      } else {
        runInAction(() => {
          this.patientSearched = true
          this.patientFound = false
          this.resetPatient()
          this.updatePatient({ isNewPatient: true, cpf: formatCpf(cleanCpf) })
          this.resetHealthInsurance()
        })
      }
    } catch (error) {
      console.error('[Store] Erro ao buscar paciente:', error)
      runInAction(() => {
        this.patientSearched = true
        this.patientFound = false
      })
      throw error
    } finally {
      runInAction(() => {
        this.isLoadingPatient = false
      })
    }
  }

  async loadTutorByCpf(cpf: string, service: PatientServiceContract) {
    const cleanCpf = cpf.replace(/\D/g, '')

    if (cleanCpf.length !== 11 || !validateCpf(cleanCpf)) {
      runInAction(() => {
        this.tutorSearched = false
        this.tutorFound = false
        this.resetTutor()
      })
      return
    }

    this.isLoadingTutor = true
    try {
      const response = await service.getPatientByCpfOrName({ cpf: cleanCpf })

      if (response && !!response?.[0]) {
        const found = response[0]

        runInAction(() => {
          this.tutorSearched = true
          this.tutorFound = true
          this.updatePatient({
            // patientId: found?.patient_id,
            ...((found.tutor || found.user) && {
              tutor: {
                cpf:
                  (found.tutor?.cpf && formatCpf(found.tutor.cpf)) ||
                  (found.user?.cpf && formatCpf(found.user.cpf)),
                name: found.tutor?.name || found?.name,
                phone: found.tutor?.phone || found.user?.phone,
                email: found.tutor?.email || found.user?.email
              }
            })
          })
        })

        if (found?.patient_id && !this.surgicalOrder.orderWasLoadedInContext) {
          await this.loadPatientHealthInsurance(found.patient_id, service)
        }
      } else {
        runInAction(() => {
          this.tutorSearched = true
          this.tutorFound = false
          this.resetTutor()
          this.updatePatient({
            tutor: {
              cpf: formatCpf(cleanCpf)
            }
          })
        })
      }
    } catch (error) {
      console.error('[Store] Erro ao buscar tutor:', error)
      throw error
    } finally {
      runInAction(() => {
        this.isLoadingTutor = false
      })
    }
  }

  async loadPatientById(patientId: number, service: PatientServiceContract) {
    if (!patientId) return

    this.isLoadingPatient = true
    try {
      const response = await service.getPatientByCpfOrName({
        patient_id: String(patientId)
      })

      if (response && !!response?.[0]) {
        const found = response[0]

        runInAction(() => {
          this.patientSearched = true
          this.patientFound = true
          this.tutorSearched = true
          this.tutorFound = true
          this.updatePatient({
            patientId: found?.patient_id,
            cpf: found.user?.cpf && formatCpf(found.user.cpf),
            name: found.name,
            birthDate: found.birthday,
            gender: found.gender,
            maritalStatus: found.marital_status as MaritalStatus,
            phone: found.user?.phone || found.tutor?.phone,
            email: found.user?.email || found.tutor?.email,
            isNewPatient: false,
            internation: found.internation,
            ...(found?.tutor && {
              tutor: {
                cpf: found.tutor?.cpf,
                name: found.tutor?.name,
                phone: found.tutor?.phone,
                email: found.tutor?.email
              }
            })
          })
        })

        if (found?.patient_id && !this.surgicalOrder.orderWasLoadedInContext) {
          await this.loadPatientHealthInsurance(found.patient_id, service)
        }
      } else {
        runInAction(() => {
          this.patientSearched = true
          this.patientFound = false
          this.tutorSearched = true
          this.tutorFound = false
          this.resetPatient()
          this.resetHealthInsurance()
        })
      }
    } catch (error) {
      console.error('[Store] Erro ao buscar paciente:', error)
      runInAction(() => {
        this.patientSearched = true
        this.patientFound = false
      })
      throw error
    } finally {
      runInAction(() => {
        this.isLoadingPatient = false
      })
    }
  }

  async loadPatientHealthInsurance(
    patientId: number,
    service: PatientServiceContract
  ) {
    try {
      const response = await service.getHealthInsuranceByPatient({
        is_active: true,
        last_health_insurance: true,
        patient_id: patientId
      })

      if (response?.length > 0) {
        const healthInfo = response[0]

        if (healthInfo.health_insurance_code && healthInfo.health_plan_code) {
          await this.loadSubPlans(
            healthInfo.health_insurance_code,
            healthInfo.health_plan_code,
            service
          )
        }

        runInAction(() => {
          this.updateHealthInsurance({
            healthInsuranceName: healthInfo.health_insurance_name,
            healthInsuranceCode: healthInfo.health_insurance_code,
            healthCard: healthInfo.health_card,
            healthPlanName: healthInfo.health_plan_name,
            healthPlanCode: healthInfo.health_plan_code,
            healthSubplanName: healthInfo.health_sub_plan_name,
            healthSubplanCode: healthInfo.health_sub_plan_code,
            ...(healthInfo.valid_thru &&
            moment.utc(healthInfo.valid_thru).isValid()
              ? {
                  validThru: moment
                    .utc(healthInfo.valid_thru)
                    .format('YYYY-MM-DD')
                }
              : {})
          })
        })
      }
    } catch (error) {
      console.error('[Store] Erro ao buscar convênio do paciente:', error)
    }
  }

  resetPatientSearch() {
    this.patientSearched = false
    this.patientFound = false
    this.resetPatient()
    this.resetTutor()
    this.resetHealthInsurance()
  }

  resetTutorSearch() {
    this.tutorSearched = false
    this.tutorFound = false
    this.resetTutor()
    this.resetHealthInsurance()
  }

  get stepRegistrationInfoFormValues(): RegistrationInfoFormValues {
    return {
      hospitalId: this.location.hospital?.hospitalId || 0,
      hospitalName: this.location.hospital?.hospitalName || '',
      surgeryCenterId: this.location.surgical_center?.surgeryCenterId || 0,
      surgeryCenterName: this.location.surgical_center.surgeryCenterName || '',
      cpf: this.patient.cpf || '',
      name: this.patient.name || '',
      birthDate: this.patient.birthDate || '',
      gender: (this.patient.gender as GenderNew) || '',
      maritalStatus: (this.patient.maritalStatus as MaritalStatus) || '',
      phone: this.patient.phone
        ? normalizePhoneForMask(this.patient.phone)
        : '',
      email: this.patient.email || '',
      healthInsuranceName: this.healthInsurance.healthInsuranceName || '',
      healthInsuranceCode: this.healthInsurance.healthInsuranceCode || 0,
      healthCard: this.healthInsurance.healthCard || '',
      validThru: this.healthInsurance.validThru || '',
      healthPlanName: this.healthInsurance.healthPlanName || '',
      healthPlanCode: this.healthInsurance.healthPlanCode || 0,
      healthSubplanName: this.healthInsurance.healthSubplanName || '',
      healthSubplanCode: this.healthInsurance.healthSubplanCode || 0,
      cooperative: this.cooperative.cooperative || '',
      tutor: {
        cpf: this.patient.tutor?.cpf || '',
        name: this.patient.tutor?.name || '',
        phone: this.patient.tutor?.phone || '',
        email: this.patient.tutor?.email || ''
      }
    }
  }

  get stepClinicalHistoryFormValues(): ClinicalHistoryFormValues {
    return {
      ...this.clinicalHistory,
      clinicalHistoryHospitalized:
        this.patient.internation?.paciente_internado ?? false
    }
  }

  get isLoading(): boolean {
    return (
      this.isLoadingSurgicalOrder ||
      this.isLoadingPatient ||
      this.isLoadingTutor ||
      this.isLoadingHospitals ||
      this.isLoadingSurgeryCenters ||
      this.isLoadingHealthPlans ||
      this.isLoadingSubPlans
    )
  }

  get isPatientComplete(): boolean {
    return !!(
      (this.patient.cpf || this.patient.patientId) &&
      this.patient.name &&
      this.patient.birthDate
    )
  }

  get isHealthInsuranceComplete(): boolean {
    return !!(
      this.healthInsurance.healthInsuranceCode &&
      this.healthInsurance.healthCard
    )
  }

  get currentStepIndex() {
    return ORDERED_STEPS.indexOf(this.currentStep)
  }

  get isFirstStep() {
    return this.currentStepIndex === 0
  }

  get isLastStep() {
    return this.currentStepIndex === ORDERED_STEPS.length - 1
  }

  get hasProcedures(): boolean {
    return this.procedures.length > 0
  }

  get hasMainProcedure(): boolean {
    return this.procedures.some((p) => p.is_main)
  }

  get hasProviders(): boolean {
    return this.providers.length > 0
  }

  get hasOpmes(): boolean {
    return this.opmes.length > 0
  }

  get hasCids(): boolean {
    return this.cids.length > 0
  }

  get hasMainCid(): boolean {
    return this.cids.some((c) => c.is_main)
  }

  get firstIncompleteStepPath(): OrderStepsPath {
    for (let i = 0; i < ORDERED_STEPS.length; i++) {
      if (!this.completedSteps.includes(i)) {
        return ORDERED_STEPS[i]
      }
    }
    // Todos os steps completos: retoma no último (ex.: laudos/documentos),
    // e não no primeiro, para não jogar o usuário de volta ao início.
    return ORDERED_STEPS[ORDERED_STEPS.length - 1]
  }

  clear() {
    this.resetLocation()
    this.resetPatient()
    this.resetTutor()
    this.resetHealthInsurance()
    this.resetCooperative()
    this.resetClinicalHistory()
    this.resetProcedures()
    this.resetProviders()
    this.resetOpmes()
    this.resetOpmeJustification()
    this.resetCids()
    this.resetSurgicalOrder()
    this.resetSubmitTrigger()
    this.resetSaveOnlyTrigger()
    this.resetIsStepValid()
    this.hospitals = []
    this.surgeryCenters = []
    this.healthPlans = []
    this.healthSubPlans = []
    this.patientSearched = false
    this.tutorSearched = false
    this.patientFound = false
    this.completedSteps = []
    this.isLoadingSurgicalOrder = false
    this.isLoadingPatient = false
    this.formKey++
  }
}

export default SurgicalOrderV2
